import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, MapPin } from "lucide-react";

export default function CustomerCompleteProfile() {
    const { user, refresh } = useAuth();
    const nav = useNavigate();
    const [districts, setDistricts] = useState([]);
    const [form, setForm] = useState({ district_slug: "", mobile_number: user?.mobile_number || "" });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { api.get("/districts").then((r) => setDistricts(r.data)).catch(() => {}); }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.district_slug) return toast.error("Pick your district");
        setSubmitting(true);
        try {
            const body = { district_slug: form.district_slug };
            if (form.mobile_number && !user?.mobile_number) body.mobile_number = form.mobile_number;
            await api.patch("/auth/customer/complete-profile", body);
            const u = await refresh();
            toast.success("Profile complete");
            nav(`/posts?district_slug=${u.district_slug}`);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-neutral-50 px-4 py-10" data-testid="customer-complete-profile">
            <div className="mx-auto max-w-md rounded-3xl border border-neutral-200 bg-white p-6 shadow-sm">
                <div className="mb-4 flex items-center gap-2">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100 text-orange-600"><MapPin size={18} /></div>
                    <div>
                        <div className="text-xs font-medium text-orange-600">One last step</div>
                        <h1 className="text-lg font-extrabold text-neutral-900">Where are you shopping from?</h1>
                    </div>
                </div>
                <p className="mb-4 text-xs text-neutral-500">We use your district to show relevant offers and trending shops nearby.</p>
                <form className="grid gap-3" onSubmit={submit}>
                    <div>
                        <Label className="text-xs">District</Label>
                        <Select value={form.district_slug || undefined} onValueChange={(v) => setForm((f) => ({ ...f, district_slug: v }))}>
                            <SelectTrigger data-testid="cp-district"><SelectValue placeholder="Pick your district" /></SelectTrigger>
                            <SelectContent>
                                {districts.map((d) => (<SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>))}
                            </SelectContent>
                        </Select>
                    </div>
                    {!user?.mobile_number && (
                        <div>
                            <Label className="text-xs">Mobile number (optional)</Label>
                            <Input inputMode="numeric" maxLength={10} value={form.mobile_number} onChange={(e) => setForm((f) => ({ ...f, mobile_number: e.target.value }))} data-testid="cp-mobile" />
                        </div>
                    )}
                    <Button type="submit" disabled={submitting} className="mt-2 h-11 nest-gradient text-white" data-testid="cp-submit">
                        {submitting && <Loader2 size={16} className="mr-2 animate-spin" />}
                        Continue
                    </Button>
                </form>
            </div>
        </div>
    );
}
